/**
 * Anthropic Provider
 *
 * Cloud LLM provider using Anthropic Claude API
 * Default: claude-3-haiku-20240307 model
 */

import { LLMProvider, GenerateOptions } from './base';

import { pluginLogger } from '@/lib/platform/utils/logger';

export class AnthropicProvider implements LLMProvider {
  name = 'Anthropic';

  constructor(
    private apiKey: string,
    private model: string = 'claude-3-haiku-20240307'
  ) {}

  async generateInsights(prompt: string, options?: GenerateOptions): Promise<string> {
    pluginLogger.log(`[Anthropic] Using model: ${this.model}`);
    const timeout = options?.timeout || 30000; // 30 seconds default
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01',
        },
        body: JSON.stringify({
          model: this.model,
          max_tokens: options?.maxTokens || 1000,
          temperature: options?.temperature || 0.7,
          messages: [
            {
              role: 'user',
              content: prompt,
            },
          ],
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        pluginLogger.error(`[Anthropic] API error for model ${this.model}:`, error);
        throw new Error(`Anthropic API error: ${error.error?.message || response.statusText}`);
      }

      const data = await response.json();
      return data.content[0].text;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Anthropic request timed out after ${timeout}ms`);
      }

      throw new Error(
        `Anthropic error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!this.apiKey) {
      return false;
    }

    // Anthropic doesn't have a simple ping endpoint
    // We just validate that API key exists
    return true;
  }
}
